<template>
  <div class="home" v-if="!loading">
    <div class="filters">
      <div class="filters__actions" :class="{'is-selecting': isSelecting}">
        <button v-if="isSelecting" class="btn btn-light no-outline filters__select btn-small"
                @click="toggleSelections">
          {{ isSelecting ? t('cancel') : t('select') }}
        </button>
        <button class="btn btn-danger no-outline filters__select btn-small"
                v-if="isSelecting"
                @click="() => isDeleteModalOpened = true"
        >
          {{ t('delete') }}
        </button>
      </div>
      <label for="search">
        <input type="search" name="search" class="search" v-model="query"/>
      </label>
    </div>
    <div class="word-list">
      <p class="word-list__summary">{{
          query ?
            `${displayedWords.length} Word(s) Found` : `${words.length} Words Total`
        }}
      </p>
      <WordListItem
        v-touch:hold="toggleSelections"
        v-for="(word, idx) in displayedWords" :key="word.id"
        @click="wordClickHandler(idx)"
        @keydown="wordClickHandler(idx)"
        :is-selecting="isSelecting" :word="word"
      />
      <GlobalSearch v-if="ShowGlobalSearch" :query="query"
                    :wordbook="wordMap"
                    @wordbook-updated="onWordbookUpdate"/>
    </div>
    <FontAwesomeIcon class="add-word" @click="addWord" :icon="faPlusCircle"/>
    <Modal v-if="isDeleteModalOpened" @modalClose="isDeleteModalOpened = false"
           @modalCancel="isDeleteModalOpened = false"
           @submit="deleteSelected">
      <template #header>
        <h2>Are you sure you want to delete
          {{ words.filter((w) => w.selected).length > 1 ? 'these words' : 'this word' }}?
        </h2>
      </template>
      <template #content>
        <ul class="word-list mt-0">
          <WordListItem v-for="(word) in words.filter((w) => w.selected)"
                        :key="word.id"
                        :is-selecting="false" :word="word"/>
        </ul>

      </template>
      <template #footer>
        <div class="button-group">
          <button class="btn no-outline btn-medium btn-light"
                  @click="isDeleteModalOpened = false">Cancel
          </button>
          <button class="btn no-outline btn-medium btn-danger"
                  @click="deleteSelected">Yes, Delete
          </button>
        </div>
      </template>
    </Modal>
  </div>
  <div class="home" v-else>
    <Spinner text="Loading..."/>
  </div>
</template>

<script lang="ts">
import { mixins, Options } from 'vue-class-component';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlusCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { WordsModel } from '@/includes/db/schemas/words.schema';
import { WordsListModel } from '@/includes/db/schemas/words-list.schema';
import { GenericObject, Selectable } from '@/includes/definitions/index.d';
import { WordText } from '@/includes/definitions/word.d';
import { databases, flags } from '@/includes/db/data/languages';
import ImageMixin from '@/includes/mixin/images.mixin';
import LanguageMixin from '@/includes/mixin/language.mixin';
import Modal from '@/components/Modal.vue';
import WordListItem from '@/components/word/WordListItem.vue';
import GlobalSearch from '@/components/word/GlobalSearch.vue';
import DictionaryDatabase from '@/includes/db/DictionaryDatabase';
import Spinner from '@/components/Spinner.vue';
import SearchMixin from '@/includes/mixin/search.mixin';
import store from '@/includes/store';

@Options({
  components: {
    Spinner,
    GlobalSearch,
    WordListItem,
    Modal,
    FontAwesomeIcon,
  },
})
export default class HomeView extends mixins(ImageMixin, LanguageMixin, SearchMixin) {
  isDeleteModalOpened = false;
  faPlusCircle = faPlusCircle;
  faSpinner = faSpinner;
  flags: GenericObject = flags;
  loading = false;
  isSelecting = false;
  hasValidDictionary = false;
  query = '';

  wordClickHandler(idx: number) {
    if (this.isSelecting) {
      this.displayedWords[idx].selected = !this.displayedWords[idx].selected;
      return;
    }
    this.$router.push(`/words/${this.displayedWords[idx].id}`);
  }

  async onWordbookUpdate(_word: Selectable<Partial<WordText>>) {
    const {
      selected,
      ...word
    } = _word;
    if (this.wordMap.has(word.text + word.meaning)) {
      await WordsModel.delete(this.wordMap.get(word.text + word.meaning).id);
      return;
    }
    await WordsModel.create(word as WordText);
    await this.refreshWordbook();
  }

  toggleSelections() {
    this.isSelecting = !this.isSelecting;
    this.words = this.words.map((w) => ({
      ...w,
      selected: false,
    }));
  }

  addWord() {
    this.$router.push({
      path: '/words/new',
      query: { prefix: this.query.trim() },
    });
  }

  async deleteSelected() {
    const ids = this.words.filter((w) => w.selected)
      .map((w) => w.id);
    await WordsModel.delete(ids);

    const idSet = new Set(ids.map(Number));
    const lists = await WordsListModel.getAll();
    const affected = lists.filter((list) => list.words.some((id) => idSet.has(id)));
    await Promise.all(affected.map((list) => WordsListModel.update({
      ...list,
      words: list.words.filter((id) => !idSet.has(id)),
    })));

    this.$router.go(0);
  }

  get ShowGlobalSearch() {
    return this.hasValidDictionary && !!this.query;
  }

  async refreshWordbook() {
    const { translationLanguage } = store.state;
    this.words = (await WordsModel.getAll())
      .reverse()
      .filter((w) => w.textLanguage === translationLanguage)
      .map((w) => ({
        ...w,
        selected: false,
      }));
  }

  async validateDictionary() {
    const { translationLanguage } = store.state;
    this.hasValidDictionary = !!translationLanguage
      && !!databases[translationLanguage]
      && (await new DictionaryDatabase(databases[translationLanguage]).info()).doc_count > 0;
  }

  async mounted() {
    this.loading = true;
    setTimeout(() => this.validateDictionary(), 1);
    const intervalId = setInterval(async () => {
      if (!WordsModel.isReady) {
        console.log('Not ready');
        return;
      }
      await this.refreshWordbook();
      clearInterval(intervalId);
      this.loading = false;
    }, 1);

    store.watch(
      (state) => state.translationLanguage,
      async () => {
        this.query = '';
        await this.refreshWordbook();
        await this.validateDictionary();
      },
    );
  }
}
</script>

<style lang="scss">
@import "../assets/styles/variables.scss";

.spinners {
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.word-list {
  list-style-type: none;
  padding-left: 0;
  margin: 0;
  margin-top: 50px;

  &__summary {
    text-align: right;
    font-size: 12px;
    font-weight: bold;
    margin-left: auto;
    margin-right: 20px;
    margin-bottom: 0;
    margin-top: 20px;
  }

  &__word {
    &-container {
      width: 100%;
    }

    &-text {
      font-weight: 500;

      &:not(.text-capitalize) {
        text-transform: lowercase;
      }
    }

    &-heading {
      display: flex;
      justify-content: space-between;
    }

    &-block {
      display: flex;

      &-meaning {
        font-size: 0.8rem;
        font-style: italic;

        &:not(.text-capitalize) {
          text-transform: lowercase;
        }
      }

      &-dot {
        font-size: .5rem;
        margin: 6px;
      }

      &-type, &-gender {
        text-transform: none;
        font-size: .7rem;
        margin-top: 2px !important;
        color: $primary-2;
      }

      &-gender {
        color: $primary
      }
    }

    &-flags-text, &-flags-meaning {
      width: 15px;
      height: 10px;
      object-fit: cover;
      border: 1px solid $dark;
      margin: 0 5px
    }
  }
}

.filters {
  &__actions {
    margin-left: 10px;

    &.is-selecting {
      :last-child {
        margin-left: 25px;
      }
    }

  }

  .search {
    border: 2px solid $secondary;
    border-radius: 8px;
    padding: 5px;
    width: 50vw;
  }

  &__select {
    width: fit-content;
  }

  :last-child {
    margin-right: 14px;
  }

  padding: 10px;
  display: flex;
  position: fixed;
  justify-content: space-between;
  width: 100%;
  background: #fff;
}

.add-word {
  position: fixed;
  bottom: 100px;
  right: 15px;
  font-size: 50px;
  color: $dark;
  background: $light;
  border-radius: 50px;
  z-index: 5;
}

</style>
